import type { Task } from './api';
import { countLabel, formatBaht } from './display';
import { plural } from './format';
import { formatWorkspaceTime, workspaceCapabilityLabel } from './workspace';

/**
 * Русские подписи для системных ключей целей, курса, вкладов и источников.
 * Применяется только к служебной справке; пользовательские заголовки не трогаем.
 */
export const GOAL_HELP_LABELS: Record<string, string> = {
  confirmed_booking: 'Подтверждённая бронь',
  goal_track: 'Ведение цели',
  track: 'Ведение цели',
  unit: 'Объект',
  leads: 'Лиды',
  availability: 'Доступность',
  market: 'Рынок',
  money: 'Деньги',
  booking: 'Бронь',
  promotion: 'Продвижение',
  approval: 'Согласование',
  owner_goal: 'Цель владельца',
};

const MACHINE_KEY = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

/** Служебный ключ → русская подпись; неизвестный машинный ключ → «Служебный шаг», иной текст не трогаем. */
export function goalHelpLabel(key: string | null | undefined): string {
  const raw = key?.trim();
  if (!raw) return '';
  const known = GOAL_HELP_LABELS[raw];
  if (known) return known;
  return MACHINE_KEY.test(raw) ? 'Служебный шаг' : raw;
}

/** Снимок источника: человеческое время Asia/Bangkok, никогда не сырой ISO. */
export function formatGoalObservedAt(value: string | null | undefined): string {
  return formatWorkspaceTime(value);
}

/** US 33,000 / EU 33.000 / spaces / 33k. Never treat a thousands comma as a decimal. */
export function parseGroupedAmount(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const compact = trimmed.replace(/[\s\u00a0\u202f']/g, '');
  const scaled = /[kк]$/i.test(compact);
  const body = compact.replace(/[kк]$/i, '');
  let value: number;
  if (/^-?\d{1,3}(,\d{3})+$/.test(body)) value = Number(body.replace(/,/g, ''));
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(body)) value = Number(body.replace(/\./g, ''));
  else if (/^-?\d+[.,]\d{1,2}$/.test(body)) value = Number(body.replace(',', '.'));
  else if (/^-?\d+$/.test(body)) value = Number(body);
  else return null;
  if (!Number.isFinite(value)) return null;
  return scaled ? value * 1000 : value;
}

const MONEY_TOKEN = String.raw`((?:\d{1,3}(?:[\s\u00a0\u202f,']\d{3})+|\d{1,3}(?:\.\d{3})+|\d+)(?:[.,]\d{1,2})?(?:\s*[kк])?)`;

function formatEmbeddedMoney(text: string): string {
  const withPeriod = text.replace(
    new RegExp(`${MONEY_TOKEN}\\s*(?:THB|฿)(?:\\s*\\/\\s*(?:мес(?:яц)?\\.?|mo(?:nth)?))?`, 'gi'),
    (match, raw: string) => {
      const value = parseGroupedAmount(raw);
      if (value == null) return match;
      return /\/\s*(?:мес|mo)/i.test(match) ? `${formatBaht(value)}/мес` : formatBaht(value);
    },
  );
  return withPeriod.replace(
    new RegExp(`${MONEY_TOKEN}\\s*\\/\\s*(?:мес(?:яц)?\\.?|mo(?:nth)?)`, 'gi'),
    (match, raw: string) => {
      const value = parseGroupedAmount(raw);
      if (value == null) return match;
      if (value < 1000 && !/[kк]/i.test(raw)) return match;
      return `${formatBaht(value)}/мес`;
    },
  );
}

function formatRatio(value: number): string {
  const digits = Number.isInteger(value) ? 0 : 2;
  return value.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: 2 });
}

function razLabel(value: number): string {
  if (!Number.isInteger(value)) return 'раза';
  return plural(value, 'раз', 'раза', 'раз');
}

function askMedianSentence(ratio: number, n: number, capitalize: boolean): string {
  const objects = countLabel(n, 'объект', 'объекта', 'объектов');
  const sparse = n < 5 ? ' · мало данных' : '';
  const lead = capitalize ? 'Наша цена' : 'наша цена';
  if (Math.abs(ratio - 1) < 0.005) {
    return `${lead} на уровне медианы похожих (${objects})${sparse}`;
  }
  const direction = ratio > 1 ? 'выше' : 'ниже';
  return `${lead} в ${formatRatio(ratio)} ${razLabel(ratio)} ${direction} медианы похожих (${objects})${sparse}`;
}

const MARKET_PRICE_ACTION = 'Проверить цену и условия';
const MARKET_PRICE_FALLBACK = `Цена выше рынка. ${MARKET_PRICE_ACTION}`;
/** `= 1.65; N=49` — LHS wording is ignored (English, Russian, or dictionary-mangled). */
const ASK_MEDIAN_NUMBERS = /=\s*(\d+(?:[.,]\d+)?)\s*[;,]\s*(?:\(\s*)?[nN]\s*=\s*(\d+)(?:\s*\))?/;

function normalizeFormulaText(text: string): string {
  return text.replace(/[\u00a0\u202f\u2007]/g, ' ').replace(/[＝]/g, '=');
}

/** Ratio and sample size from a ready-made Market Intelligence reason string. */
export function parseAskMedianFormula(text: string): { ratio: number; n: number } | null {
  const match = ASK_MEDIAN_NUMBERS.exec(normalizeFormulaText(text));
  if (!match) return null;
  const ratio = Number(match[1]!.replace(',', '.'));
  const n = Number(match[2]);
  if (!Number.isFinite(ratio) || !Number.isFinite(n)) return null;
  return { ratio, n };
}

function looksLikeMedianFormula(text: string): boolean {
  const value = normalizeFormulaText(text);
  return /=\s*\S/.test(value) && /[nN]\s*=/.test(value);
}

/**
 * Formula first — the «ask» dictionary used to garble live Market Intelligence reasons.
 * Match `= X; N=Y` regardless of English / already-Russian / mangled LHS.
 */
function rewriteAskMedian(text: string): string {
  const normalized = normalizeFormulaText(text);
  const pattern = new RegExp(
    String.raw`(?:[A-Z0-9]{2,}:\s*)?[^\n=]{0,160}?${ASK_MEDIAN_NUMBERS.source}`,
    'gi',
  );
  return normalized.replace(pattern, (match, ratioRaw: string, nRaw: string, offset: number) => {
    const ratio = Number(String(ratioRaw).replace(',', '.'));
    const n = Number(nRaw);
    if (!Number.isFinite(ratio) || !Number.isFinite(n)) return match;
    const unit = /^(?:[A-Z0-9]{2,}:\s*)/i.exec(match)?.[0] ?? '';
    const prefix = normalized.slice(0, offset) + unit;
    return unit + askMedianSentence(ratio, n, !/:\s*$/.test(prefix));
  });
}

/** ASCII-only word boundary: `\b` is the same for Latin, but we keep replacements off formula tokens. */
function wholeWord(text: string, word: string, replacement: string, flags = 'g'): string {
  return text.replace(new RegExp(`(?<![A-Za-z0-9_])${word}(?![A-Za-z0-9_])`, flags), replacement);
}

/** System-generated copy only; never normalize user-authored requests or titles. */
export function humanSystemText(text: string): string {
  let value = rewriteAskMedian(text)
    .replace(/canonical Rent\/Oleg/g, 'данные аренды')
    .replace(/Fin Roadmap(?:\s+Rent\/Oleg)?/g, 'Финплан')
    .replace(/\s*Rent\/Oleg/g, '')
    .replace(/PgBookingWriter(?:\(core\.bookings\))?/g, 'основной реестр броней')
    .replace(/core\.bookings/g, 'реестр броней')
    .replace(/\bcanonical\b/g, 'основные данные')
    .replace(/FaceID(?:\s+(?:guard|gap))?/g, 'проверка личности')
    .replace(/проверка личности не пройден(?!а)/g, 'проверка личности не пройдена')
    .replace(/проверить Rental Truth/g, 'проверить Правду аренды')
    .replace(/Rental Truth/g, 'Правда аренды')
    .replace(/Rental Platform for Co-agent/gi, 'Платформа аренды для агентов')
    .replace(/Money Road\s*Map/gi, 'Финплан')
    .replace(/Closed Loop/gi, 'Полный цикл')
    .replace(/ETAGI PATTAYA/gi, 'Этажи Паттайя')
    .replace(/Jack OS · New arch/gi, 'Jack OS · новая архитектура')
    .replace(/New arch/gi, 'новая архитектура')
    .replace(/\bcomparables\b/gi, 'похожие объекты')
    .replace(/OWNER[ _]GOAL/gi, 'Цель владельца')
    .replace(/\bASKING\b/gi, 'цена владельца');
  value = wholeWord(value, 'ask', 'цена владельца', 'gi');
  value = value
    .replace(/\bOLEG\b/g, 'Олег')
    .replace(/\bOleg\b/g, 'Олег')
    .replace(/ОЛЕГ/g, 'Олег')
    .replace(/\bOperations\b/g, 'Операции')
    .replace(/\bNina\b/g, 'Нина')
    .replace(/\bLena\b/g, 'Лена')
    .replace(/\bKolya\b/g, 'Коля')
    .replace(/\bZhora\b/g, 'Жора')
    .replace(/\bSergey\b/g, 'Сергей')
    .replace(/\bMarina\b/g, 'Марина')
    .replace(/TECH_REQUEST|TECH ticket/g, 'заявка на обслуживание')
    .replace(/\bevidence\b/g, 'подтверждение результата')
    .replace(/\bBLOCKED\b/g, 'Остановлено: нужна проверка')
    .replace(/\b(?:exec|run)_[a-zA-Z0-9_-]+\b/g, 'связанное выполнение')
    .replace(/\bRaw JSON\b/gi, 'технические данные')
    .replace(/\bOWNER[ _]EXPENSE\b/gi, 'Расход владельца')
    .replace(/\bAGENT_PAYABLE\b/g, 'обязательства перед агентами')
    .replace(/\bOWNER_PAYABLE\b/g, 'обязательства перед владельцами')
    .replace(/\bDEPOSIT_LIABILITY\b/g, 'депозиты гостей')
    .replace(/Без объекта/g, 'Объект не указан')
    .replace(/\bPOSTED-(?=проводк)/g, '')
    .replace(/\bPOSTED\b/g, 'проведено')
    .replace(/ledger-проводк/gi, 'учётн')
    .replace(/\b(?:canonical )?ledger\b/gi, 'учёт')
    .replace(/\bRuns\b/g, 'ходы работы')
    .replace(/\bRun\b/g, 'ход работы')
    .replace(/\b(?:ops|workspace)\.[a-z_]+\.[a-z_]+\b/g, capability => workspaceCapabilityLabel(capability))
    .replace(/\b(?:confirmed_booking|goal_track|availability|promotion|approval|leads|market|money|booking|unit|owner_goal)\b/g, key => goalHelpLabel(key));
  return formatEmbeddedMoney(value);
}

/**
 * Рынок card in «Требует вас»: prefer structured ratio/N from the payload,
 * else parse `= X; N=Y`, else a short fallback when the string still looks like a formula.
 */
export function formatMarketAttentionSummary(
  summary: string | null | undefined,
  structured?: { ratio?: number | null; n?: number | null },
): string {
  const ratio = structured?.ratio;
  const n = structured?.n;
  const fromData = ratio != null && n != null && Number.isFinite(ratio) && Number.isFinite(n)
    ? { ratio, n }
    : parseAskMedianFormula(summary ?? '');
  if (fromData) return `${askMedianSentence(fromData.ratio, fromData.n, false)}. ${MARKET_PRICE_ACTION}`;
  const text = summary?.trim() ?? '';
  if (looksLikeMedianFormula(text)) return MARKET_PRICE_FALLBACK;
  return text ? humanSystemText(text) : '';
}

/** Shared dictionary for task titles on every screen, including the calendar. */
export function taskDisplayTitle(task: Pick<Task, 'title' | 'description'>): string {
  return humanSystemText(task.title);
}

export function businessStatusLabel(status: string | null | undefined): string {
  const labels: Record<string, string> = {
    confirmed: 'Подтверждено', CONFIRMED: 'Подтверждено', ACTIVE: 'Активно', active: 'Активно',
    CHECKED_IN: 'Гости заселены', CHECKED_OUT: 'Гости выехали', DEPOSIT_WAITING: 'Ожидается депозит', HOLD: 'Бронь удерживается',
    PENDING: 'Ожидает подтверждения', pending: 'Ожидает подтверждения', UNPAID: 'Не оплачено',
    PAID: 'Оплачено', PARTIAL: 'Оплачено частично', HELD: 'Депозит удерживается',
    REFUNDED: 'Возвращено', CANCELLED: 'Отменено', cancelled: 'Отменено',
    NEW: 'Создано', completed: 'Завершено', COMPLETED: 'Завершено', created: 'Создано',
  };
  return status && labels[status] ? labels[status] : 'Статус пока не известен';
}

const APPROVAL_ACTION_LABELS: Record<string, string> = {
  repair: 'Ремонт',
  maintenance: 'Ремонт',
  technical: 'Ремонт',
  tech_request: 'Заявка на обслуживание',
  delete_resource: 'Удалить запись',
  owner_expense: 'Расход владельца',
  payment: 'Платёж',
  payment_allocate: 'Распределить платёж',
  allocate: 'Распределить платёж',
  booking: 'Бронь',
  booking_payment: 'Оплата брони',
  transfer: 'Перемещение денег',
  deposit: 'Депозит',
  media_review: 'Проверка файла',
  skip_photo: 'Пропустить фото',
  send: 'Отправить сообщение',
  apply: 'Выполнить',
  approve: 'Подтвердить',
  reject: 'Отклонить',
};

const ACTION_WORDS: Record<string, string> = {
  delete: 'удалить', resource: 'запись', repair: 'ремонт', maintenance: 'ремонт',
  technical: 'ремонт', tech: 'обслуживание', request: 'заявка', owner: 'владельца',
  expense: 'расход', payment: 'платёж', allocate: 'распределить', booking: 'бронь',
  transfer: 'перемещение', deposit: 'депозит', media: 'файл', review: 'проверка',
  skip: 'пропустить', photo: 'фото', send: 'отправить', apply: 'выполнить',
  approve: 'подтвердить', reject: 'отклонить', cash: 'касса', account: 'счёт',
};

const OPAQUE_ID = /^(?:exec|appr|oprun|ent|run)_[a-z0-9_-]+$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const READABLE_CODE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,47}$/;

function titleCaseRu(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

/** Машинный ключ действия согласования → русская фраза; сырой ключ владельцу не показываем. */
export function approvalActionLabel(action: string | null | undefined): string {
  const raw = action?.trim() ?? '';
  if (!raw) return 'Не указано';
  const known = APPROVAL_ACTION_LABELS[raw] ?? APPROVAL_ACTION_LABELS[raw.toLowerCase()];
  if (known) return known;
  if (raw.includes('.')) {
    const capability = workspaceCapabilityLabel(raw);
    if (capability !== raw) return capability;
  }
  if (/[А-Яа-яЁё]/.test(raw)) return humanSystemText(raw);
  const parts = raw.toLowerCase().split(/[._/-]+/).filter(Boolean);
  const words = parts.map(part => ACTION_WORDS[part]);
  if (parts.length > 0 && words.every(Boolean)) return titleCaseRu(words.join(' '));
  return 'Служебное действие';
}

/** Цель согласования: код объекта как есть, технический путь — без префикса или общей пометки. */
export function approvalTargetLabel(target: string | null | undefined): string {
  const raw = target?.trim() ?? '';
  if (!raw) return 'Не указано';
  const stripped = raw.replace(/^(?:resource|unit|object|booking|lead|target|record)[/:]/i, '').trim();
  if (!stripped) return 'Связанная запись';
  if (OPAQUE_ID.test(stripped) || UUID.test(stripped)) return 'Связанная запись';
  if (READABLE_CODE.test(stripped) && !MACHINE_KEY.test(stripped)) return stripped;
  if (/[А-Яа-яЁё]/.test(stripped)) return humanSystemText(stripped);
  return 'Связанная запись';
}
